import Alpine from 'alpinejs';
import { pDefer } from '~/js/utilities/promise';
import { request } from '~js/utilities';
import {
  formatPrice,
  fetchProducts,
} from '~js/utilities/products-and-collections';

const CART_GET_URL = window.Shopify.routes.root + 'cart.js';
const CART_ADD_URL = window.Shopify.routes.root + 'cart/add.js';
const CART_UPDATE_URL = window.Shopify.routes.root + 'cart/update.js';
const CART_CHANGE_URL = window.Shopify.routes.root + 'cart/change.js';
const CART_REWARD_TYPE = window.CART_REWARD_TYPE;

export const cartDataInitial = pDefer();

export const cartStore = () => ({
  cartData: null,
  giftBoxProductData: null,
  gwpProduct: null,
  collectionGWP: null,
  percentageReachedCollectionGwp: 0,
  selectedCollectionGwpVariantId: null,
  async init() {
    await this.fetchCart();
    cartDataInitial.resolve(this.cartData);

    // this.$watch('cartData', () => {
    //   this.$dispatch("cart-updated");
    // })

    const ids = [
      window.GIFT_BOX_PRODUCT_ID,
      window.GIFT_WITH_PURCHASE_PRODUCT_ID,
      window.COLLECTION_GWP_PRODUCT_ID,
    ]
      .filter(Boolean)
      .filter((id) => id !== 0); // Filter out invalid IDs (Zero, null)
    const productData = await fetchProducts(ids);
    this.giftBoxProductData = productData[window.GIFT_BOX_PRODUCT_ID];
    this.gwpProduct = productData[window.GIFT_WITH_PURCHASE_PRODUCT_ID];
    this.collectionGWP = productData[window.COLLECTION_GWP_PRODUCT_ID];
  },
  /**
   * START: FIRST-CLASS ACTIONS
   * These interact with AJAX API and must set cartData
   * consequentially.
   */
  async fetchCart() {
    const url = new URL(window.location.href);
    url.pathname = CART_GET_URL;
    url.search = new URLSearchParams({
      t: Date.now(),
    });

    const params = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const data = await request(url, params);
    this.cartData = data;
  },

  async addToCart(body) {
    await request(CART_ADD_URL, {
      method: 'post',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    });

    this.fetchCart();
  },
  async addToCartGWP(body) {
    await request(CART_ADD_URL, {
      method: 'post',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    });

    this.fetchCart();
  },

  async updateCart(body) {
    const url = CART_UPDATE_URL;
    const params = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    };

    const data = await request(url, params);
    // If only remaining item is Gift Box, remove that too
    if (
      data.items.length === 1 &&
      data.items[0].product_id === window.GIFT_BOX_PRODUCT_ID &&
      !this.clearingGiftBox
    ) {
      // note, recursive
      // should only be entered once, clearingGiftBox is saftey flag against infinite loop
      this.clearingGiftBox = true;
      await this.removeLineItem(data.items[0].key);
      await this.clearGiftBoxAttributes();
      this.clearingGiftBox = false;
    } else {
      // normal case
      this.cartData = data;
    }
    this.clearingGiftBox = false;
  },

  async changeLineItem(body) {
    const url = CART_CHANGE_URL;
    const params = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    };

    const data = await request(url, params);
    this.cartData = data;
  },

  /**
   * SECOND-CLASS ACTIONS
   * These call POST method FIRST-CLASS ACTIONS with predetermined bodies
   * They should not handle response data, but do return awaitable promises.
   */
  async removeLineItem(key) {
    return await this.changeLineItem({
      id: key,
      quantity: 0,
    });
  },

  async clearGiftBoxAttributes() {
    const cartAttributes = { ...this.cartData.attributes };
    cartAttributes['gift-wrapping'] = null;
    cartAttributes['gift-note'] = null;

    return await this.updateCart({
      attributes: cartAttributes,
    });
  },

  get normalizedCartQty() {
    return this.cartData.items
      .filter((item) => item.product_id !== window.GIFT_BOX_PRODUCT_ID)
      .reduce((acc, item) => acc + item.quantity, 0);
  },

  get lineItemsNormalized() {
    // currently normalization consists only of ensuring
    // that any gift box product is last in order
    return [...this.cartData.items].sort((a, b) =>
      a.product_id === window.GIFT_BOX_PRODUCT_ID
        ? 1
        : b.product_id === window.GIFT_BOX_PRODUCT_ID
        ? -1
        : 0
    );
  },

  get giftBoxItemOnCart() {
    return this.cartData.items.find(
      (item) => item.product_id === window.GIFT_BOX_PRODUCT_ID
    );
  },

  get giftBoxPrice() {
    const { giftBoxProductData } = this;
    if (giftBoxProductData) {
      return giftBoxProductData.variants[0].priceFormatted;
    }

    return '';
  },
});

export const cartComponent = (
  freePerkThreshold,
  freePerkProgressMessage,
  freePerkMessage,
  free2DayPerkThreshold,
  free2DayPerkProgressMessage,
  free2DayPerkMessage,
  ssRecommendationProfile,
  firstIcon,
  secondIcon
) => ({
  freePerkThreshold,
  freePerkProgressMessage,
  freePerkMessage,
  free2DayPerkThreshold,
  free2DayPerkProgressMessage,
  free2DayPerkMessage,
  firstIcon,
  secondIcon,
  cartHeader() {
    return `Shopping Bag${
      this.$store.cart.cartData
        ? ` (${this.$store.cart.normalizedCartQty})`
        : ''
    }`;
  },
  // US localization only: START
  get enableFreeShippingProgress() {
    return window.Shopify.country === 'US';
  },

  get eligibleForFreeShipping() {
    return this.totalForProgressCalculation >= this.freePerkThreshold;
  },
  get showGWP() {
    return (
      window.GIFT_WITH_PURCHASE_SHOW_IN_CART &&
      window.GIFT_WITH_PURCHASE_PRODUCT_ID &&
      window.GIFT_WITH_PURCHASE_PRODUCT_ID !== 0
    );
  },

  get eligibleForFree2DayShipping() {
    return this.totalForProgressCalculation >= this.free2DayPerkThreshold;
  },
  get totalForProgressCalculation() {
    // Do not include possible gift box in calculation
    /*
    let excludedUnits = 1;
    
    return (() => {
      const cartItems = this.$store.cart.cartData?.items || [];

      const hasFreeGift = cartItems.some(
        item => item.product_id === window.GIFT_WITH_PURCHASE_PRODUCT_ID && item.final_price === 0
      );

      return cartItems.reduce((acc, item) => {
        const isExcludedProduct =
          item.product_id === window.GIFT_BOX_PRODUCT_ID ||
          item.product_id === window.GIFT_WITH_PURCHASE_PRODUCT_ID;

        const quantity = item.quantity;
        const price = item.final_price;

        if (isExcludedProduct && excludedUnits > 0 && !hasFreeGift) {
          const unitsToExclude = Math.min(quantity, excludedUnits);
          const unitsToInclude = quantity - unitsToExclude;
          return acc + (unitsToInclude * price);
        }

        return acc + (quantity * price);
      }, 0) / 100;
    })();*/

    return (() => {
      const cartItems = this.$store.cart.cartData?.items || [];

      const hasFreeGift = cartItems.some(
        (item) =>
          item.product_id === window.GIFT_WITH_PURCHASE_PRODUCT_ID &&
          item.final_price === 0 &&
          item.properties?.__isGiftWithPurchase === 'GWP'
      );

      let excludedUnits = 0;

      return (
        cartItems.reduce((acc, item) => {
          const isExcludedProduct =
            item.product_id === window.GIFT_BOX_PRODUCT_ID ||
            item.product_id === window.GIFT_WITH_PURCHASE_PRODUCT_ID;

          const quantity = item.quantity;
          const price = item.final_price;

          if (isExcludedProduct && excludedUnits > 0 && !hasFreeGift) {
            const unitsToExclude = Math.min(quantity, excludedUnits);
            const unitsToInclude = quantity - unitsToExclude;
            return acc + unitsToInclude * price;
          }

          return acc + quantity * price;
        }, 0) / 100
      );
    })();
    /*
    return (
      this.$store.cart.cartData?.items.reduce((acc, item) => {
        const isExcludedProduct = item.product_id === window.GIFT_BOX_PRODUCT_ID || item.product_id === window.GIFT_WITH_PURCHASE_PRODUCT_ID;
        const quantity = item.quantity;
        const price = item.final_price;
        if (isExcludedProduct && excludedUnits > 0) {
          const unitsToExclude = Math.min(quantity, excludedUnits);
          const unitsToInclude = quantity - unitsToExclude;
          return acc + (unitsToInclude * price);
        }
        return acc + (quantity * price);
      },0) / 100
    )
  */
    return (
      this.$store.cart.cartData?.items
        .filter(
          (item) =>
            item.product_id !== window.GIFT_BOX_PRODUCT_ID ||
            item.product_id !== window.GIFT_WITH_PURCHASE_PRODUCT_ID
        )
        .reduce((acc, item) => acc + item.final_line_price, 0) / 100
    );
  },
  get currentVariantCollectionGwp() {
    if (this.$store.cart.selectedCollectionGwpVariantId) {
      return this.$store.cart.selectedCollectionGwpVariantId;
    }
  },
  minimumAmountCollectionGwp() {
    if (this.$store.cart.collectionGWP) {
      return `$${this.$store.cart.collectionGWP?.gwpProductAmount?.value || 0}`;
    }
    return 0;
  },
  get textAboveCollectionGwpProgressBar() {
    const percentageTotal = Math.min(
      this.$store.cart.percentageReachedCollectionGwp,
      100
    );
    switch (percentageTotal) {
      case 0:
        return window.ZERO_COLLECTION_GWP_PROGRESS_MESSAGE;
      case 100:
        const isCollectionGwpInCart = this.$store.cart.cartData?.items?.some(
          (item) =>
            item.product_id === window.COLLECTION_GWP_PRODUCT_ID &&
            item.properties?.__collectionFreeGift === '__collectionFreeGift'
        );
        return isCollectionGwpInCart
          ? window.GIFT_ADDED_COLLECTION_GWP_PROGRESS_MESSAGE
          : this.$store.cart.collectionGWP?.variants.length === 1
          ? window.GIFT_ADDED_COLLECTION_GWP_PROGRESS_MESSAGE
          : window.FULL_COLLECTION_GWP_PROGRESS_MESSAGE;
      default:
        const remainingPercentage = (100 - percentageTotal) / 100;
        const remainingAmount =
          remainingPercentage *
          parseFloat(this.$store.cart.collectionGWP?.gwpProductAmount?.value);
        return window.PARTIAL_COLLECTION_GWP_PROGRESS_MESSAGE.replace(
          '{amount}',
          remainingAmount.toFixed(2)
        );
    }
  },
  async totalProgressCollectionGwp() {
    if (
      this.$store.cart.collectionGWP &&
      window.COLLECTION_GWP_ENABLE &&
      this.$store.cart.collectionGWP.availableForSale
    ) {
      try {
        const cartItems = this.$store.cart.cartData?.items || [];
        const productWithTags = await fetchProducts(
          cartItems.map((item) => item.product_id)
        );

        let total = 0;
        for (const item of cartItems) {
          if (
            productWithTags[item.product_id]?.tags &&
            productWithTags[item.product_id]?.tags.includes('Holiday Shoppe')
          ) {
            total += item.final_line_price || 0;
          }
        }
        const totalAmount = total / 100;
        let visibleComponent = true;

        if (
          this.$store.cart.collectionGWP !== null &&
          this.$store.cart.collectionGWP.availableForSale
        ) {
          const minimumAmount =
            parseFloat(
              this.$store.cart.collectionGWP?.gwpProductAmount?.value
            ) || 0;
          if (minimumAmount === 0) {
            return 0;
          }
          const percentageReached = (totalAmount / minimumAmount) * 100;
          if (this.$store.cart.collectionGWP?.variants) {
            if (this.$store.cart.collectionGWP?.variants.length === 1) {
              visibleComponent = false;
            }
          }
          const isCollectionGwpInCart = this.$store.cart.cartData?.items?.some(
            (item) =>
              item.product_id === window.COLLECTION_GWP_PRODUCT_ID &&
              item.properties?.__collectionFreeGift === '__collectionFreeGift'
          );
          if (
            percentageReached >= 100 &&
            !visibleComponent &&
            !isCollectionGwpInCart
          ) {
            await this.$store.cart.addToCartGWP({
              items: [
                {
                  id: this.$store.cart.collectionGWP?.variants[0].id.replace(
                    'gid://shopify/ProductVariant/',
                    ''
                  ),
                  properties: {
                    __collectionFreeGift: '__collectionFreeGift',
                  },
                  quantity: 1,
                },
              ],
            });
          }
          //Second Phase
          if (
            percentageReached >= 100 &&
            visibleComponent &&
            !isCollectionGwpInCart
          ) {
          }
          this.$store.cart.percentageReachedCollectionGwp = percentageReached;
          return percentageReached;
        }
      } catch (error) {
        console.error('Exception:', error);
        return 0;
      }
    } else {
      const isCollectionGwpInCart = this.$store.cart.cartData?.items?.some(
        (item) =>
          item.properties?.__collectionFreeGift === '__collectionFreeGift'
      );
      if (isCollectionGwpInCart && !window.COLLECTION_GWP_ENABLE) {
        const updates = {};
        const collectionGwpItem = this.$store.cart.cartData.items.find(
          (item) =>
            item.properties?.__collectionFreeGift === '__collectionFreeGift'
        );
        updates[collectionGwpItem.key] = 0;
        await this.$store.cart.updateCart({ updates });
      }
    }
  },
  get firstOptionCollectionGwpVariant() {
    if (
      this.$store.cart.collectionGWP?.options &&
      this.$store.cart.collectionGWP?.options.length > 0
    ) {
      return this.$store.cart.collectionGWP?.options[0].name + ':';
    }
    return '';
  },
  async selectedCollectionGwpVariant(event) {
    const selectedId = event.target.value;
    if (selectedId === '') {
      this.$store.cart.selectedCollectionGwpVariantId = null;
      return;
    }
    if (!selectedId) {
      return;
    }
    this.$store.cart.selectedCollectionGwpVariantId = selectedId;
  },
  get collectionGwpEnable() {
    return window.COLLECTION_GWP_ENABLE || false;
  },
  get priceCollectionGwpVariant() {
    if (this.$store.cart.collectionGWP) {
      return formatPrice(
        {
          amount:
            this.$store.cart.collectionGWP?.priceRange?.maxVariantPrice.amount,
          currencyCode:
            this.$store.cart.collectionGWP?.priceRange?.maxVariantPrice
              .currencyCode,
        },
        false
      );
    }
    return '';
  },
  get priceCollectionGwpVariantFree() {
    return 'FREE';
  },
  get showVariantSelection() {
    if (this.$store.cart.collectionGWP) {
      const available =
        this.$store.cart.collectionGWP !== null &&
        this.$store.cart.collectionGWP.availableForSale;
      return available && this.collectionGwpEnable;
    }
  },
  get showAddToCartButton() {
    if (this.$store.cart.collectionGWP) {
      const isCollectionGwpInCart = this.$store.cart.cartData?.items?.some(
        (item) =>
          item.product_id === window.COLLECTION_GWP_PRODUCT_ID &&
          item.properties?.__collectionFreeGift === '__collectionFreeGift'
      );
      const available =
        this.$store.cart.collectionGWP !== null &&
        this.$store.cart.collectionGWP.availableForSale;
      return (
        this.$store.cart.percentageReachedCollectionGwp >= 100 &&
        available &&
        this.collectionGwpEnable &&
        !isCollectionGwpInCart
      );
    }
    return false;
  },
  get showLockedIcon() {
    if (this.$store.cart.collectionGWP) {
      return this.$store.cart.percentageReachedCollectionGwp < 100;
    }
    return false;
  },
  get collectionGwpTitle() {
    if (this.$store.cart.collectionGWP) {
      if (this.$store.cart.percentageReachedCollectionGwp == 0) {
      }

      return this.$store.cart.collectionGWP.title;
    }
    return '';
  },
  get collectionGwpLink() {
    if (this.$store.cart.collectionGWP) {
      return this.$store.cart.collectionGWP.onlineStoreUrl;
    }
    return '';
  },
  get collectionGwpImage() {
    if (this.$store.cart.collectionGWP) {
      return this.$store.cart.collectionGWP.images.nodes[0].url;
    }
    return '';
  },
  get collectionGwpVariants() {
    if (this.$store.cart.collectionGWP?.variants) {
      return this.$store.cart.collectionGWP?.variants
        .map((variant) => {
          return {
            id: variant.id,
            title: variant.title,
            price: variant.priceFormatted,
            image: variant.image ? variant.image.url : '',
            options: variant.selectedOptions.map((option) => option.value),
            availableForSale: variant.availableForSale,
          };
        })
        .filter((variant) => variant.availableForSale);
    }
  },
  async collectionGwpAddToCart() {
    const variantsAvailable = this.$store.cart.collectionGWP?.variants.filter(
      (variant) => variant.availableForSale
    );

    if (
      this.$store.cart.selectedCollectionGwpVariantId === null &&
      variantsAvailable[0]
    ) {
      this.$store.cart.selectedCollectionGwpVariantId = variantsAvailable[0].id;
    }
    if (this.$store.cart.selectedCollectionGwpVariantId !== null) {
      await this.$store.cart.addToCartGWP({
        items: [
          {
            id: this.$store.cart.selectedCollectionGwpVariantId.replace(
              'gid://shopify/ProductVariant/',
              ''
            ),
            properties: {
              __collectionFreeGift: '__collectionFreeGift',
            },
            quantity: 1,
          },
        ],
      });
    }
  },
  get progressMessage() {
    if (this.totalForProgressCalculation < this.freePerkThreshold) {
      const remaining =
        this.freePerkThreshold - this.totalForProgressCalculation;

      return this.freePerkProgressMessage.replace(
        '{amount}',
        `$${remaining.toFixed(2)}`
      );
    } else {
      if (CART_REWARD_TYPE == 'one-reward') {
        return this.freePerkMessage;
      } else {
        if (this.totalForProgressCalculation < this.free2DayPerkThreshold) {
          const remaining =
            this.free2DayPerkThreshold - this.totalForProgressCalculation;

          return this.free2DayPerkProgressMessage.replace(
            '{amount}',
            `$${remaining.toFixed(2)}`
          );
        } else {
          return this.free2DayPerkMessage;
        }
      }
    }
  },

  get progressValue() {
    return (
      (this.totalForProgressCalculation /
        (CART_REWARD_TYPE == 'one-reward'
          ? this.freePerkThreshold
          : this.free2DayPerkThreshold)) *
      100
    );
    // return (this.totalForProgressCalculation / this.free2DayPerkThreshold) * 100
  },

  get iconProgressPosition() {
    const progress = this.progressValue > 100 ? 100 : this.progressValue;
    if (progress === 0) return '0%';
    return `calc(${progress}% - 16px)`;
  },

  get progressPercentage() {
    return `${this.progressValue}%`;
  },
  get collectionPercentageReachedCollectionGwp() {
    if (this.$store.cart.percentageReachedCollectionGwp) {
      return `${Math.min(
        this.$store.cart.percentageReachedCollectionGwp,
        100
      )}%`;
    }
    return 0;
  },

  get freeShippingPercentage() {
    return `${(this.freePerkThreshold / this.free2DayPerkThreshold) * 100}%`;
  },
  // US localization only: END

  get cartHasDiscounts() {
    return this.$store.cart.cartData.total_discount > 0;
  },

  get discountTotal() {
    if (
      this.$store.cart.cartData.currency &&
      this.$store.cart.cartData.total_discount
    ) {
      return formatPrice({
        currencyCode: this.$store.cart.cartData.currency,
        amount: this.$store.cart.cartData.total_discount
          ? this.$store.cart.cartData.total_discount / -100
          : 0,
      });
    } else return '';
  },

  get subtotal() {
    if (this.$store.cart.cartData.currency) {
      return formatPrice({
        currencyCode: this.$store.cart.cartData.currency,
        amount: this.$store.cart.cartData.total_price
          ? this.$store.cart.cartData.total_price / 100
          : 0,
      });
    } else return '';
  },

  get gwpTitle() {
    if (this.$store.cart.gwpProduct) {
      return this.$store.cart.gwpProduct.title;
    }
    return '';
  },
  get gwpLink() {
    if (this.$store.cart.gwpProduct) {
      return this.$store.cart.gwpProduct.onlineStoreUrl;
    }
    return '';
  },
  get gwpSelect() {
    if (this.$store.cart.gwpProduct?.options) {
      return 'Select Gift';
    }

    return '';
  },
  get gwpImage() {
    if (this.$store.cart.gwpProduct) {
      return this.$store.cart.gwpProduct.images.nodes[0].url;
    }
    return '';
  },
  get gwpVariants() {
    if (this.$store.cart.gwpProduct?.variants) {
      return this.$store.cart.gwpProduct?.variants
        .map((variant) => {
          return {
            id: variant.id,
            title: variant.title,
            price: variant.priceFormatted,
            image: variant.image ? variant.image.url : '',
            options: variant.selectedOptions.map((option) => option.value),
            availableForSale: variant.availableForSale,
          };
        })
        .filter((variant) => variant.availableForSale);
    }
  },
  async handleClick(variant) {
    if (!variant) {
      return;
    }

    await this.$store.cart.addToCartGWP({
      items: [
        {
          id: variant.replace('gid://shopify/ProductVariant/', ''),
          properties: {
            __isGiftWithPurchase: 'GWP',
            __minimumAmountRequired:
              window.GIFT_WITH_PURCHASE_MINIMUM_CART_VALUE,
          },
          quantity: 1,
        },
      ],
    });
  },
  async selectedVariant(event) {
    const selectedId = event.target.value;
    if (!selectedId) {
      return;
    }
    await this.$store.cart.addToCartGWP({
      items: [
        {
          id: selectedId.replace('gid://shopify/ProductVariant/', ''),
          properties: {
            __isGiftWithPurchase: 'GWP',
            __minimumAmountRequired:
              window.GIFT_WITH_PURCHASE_MINIMUM_CART_VALUE,
          },
          quantity: 1,
        },
      ],
    });
  },
  async shouldBeVisible() {
    const isZeroValueInCart = this.$store.cart.cartData?.items?.some(
      (item) =>
        item.product_id === window.GIFT_WITH_PURCHASE_PRODUCT_ID &&
        item.properties?.__isGiftWithPurchase === 'GWP'
    );
    let visible = true;
    if (this.$store.cart.gwpProduct?.variants) {
      if (this.$store.cart.gwpProduct?.variants.length === 1) {
        visible = false;
      }
    }

    if (
      !isZeroValueInCart &&
      !visible &&
      this.totalForProgressCalculation >=
        window.GIFT_WITH_PURCHASE_MINIMUM_CART_VALUE &&
      this.showGWP
    ) {
      if (this.$store.cart.gwpProduct?.variants) {
        if (
          this.$store.cart.gwpProduct?.variants.length === 1 &&
          !this.$store.cart?.cartData?.items?.some(
            (item) =>
              item.product_id === window.GIFT_WITH_PURCHASE_PRODUCT_ID &&
              item.final_price == 0 &&
              item.properties?.__isGiftWithPurchase === 'GWP'
          )
        ) {
          await this.$store.cart.addToCartGWP({
            items: [
              {
                id: this.$store.cart.gwpProduct?.variants[0].id.replace(
                  'gid://shopify/ProductVariant/',
                  ''
                ),
                properties: {
                  __isGiftWithPurchase: 'GWP',
                  __minimumAmountRequired:
                    window.GIFT_WITH_PURCHASE_MINIMUM_CART_VALUE,
                },
                quantity: 1,
              },
            ],
          });
        }
      }
    }
    const val = !isZeroValueInCart && visible;
    return (
      this.totalForProgressCalculation >=
        window.GIFT_WITH_PURCHASE_MINIMUM_CART_VALUE &&
      this.showGWP &&
      val
    );
  },

  init() {
    // noop
  },
});
